import { forkJoin, Subscription } from 'rxjs';
import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, MenuItem, SelectItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { LANGUAGES } from 'projects/core/src/lib/constants';
import { RatingtypeModel } from 'projects/core/src/lib/models/ratingtype.model';
import { TEMPLATETYPES, TemplateModel } from 'projects/core/src/lib/models/template.model';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { ResourceModel } from 'projects/core/src/lib/models/resource.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

@Component({
  selector: 'app-template-basedata',
  templateUrl: './template-basedata.component.html',
  styleUrls: ['./template-basedata.component.scss']
})
export class TemplateBasedataComponent extends BaseForm implements OnInit, OnDestroy {

  @Input() template: TemplateModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public items: Array<MenuItem> = [];
  public languages: Array<SelectItem> = [];
  public types: Array<SelectItem> = [];
  public ratingtypes: Array<any> = [];
  public locations: Array<LocationModel> = [];
  public reportTemplates: Array<ResourceModel> = [];
  public optionsLocations: Array<LocationModel> = [];
  public optionsCategories: Array<string> = [];
  public subscriptions: Array<Subscription> = [];
  public inheritVisible = false;

  public form: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl('', [Validators.required]),
    ratingtype: new UntypedFormControl('', [Validators.required]),
    type: new UntypedFormControl('', [Validators.required]),
    language: new UntypedFormControl('', [Validators.required]),
    template_category: new UntypedFormControl(''),
    documents_visible_observer: new UntypedFormControl(false),
    mailtext: new UntypedFormControl(''),
    onsite: new UntypedFormControl(false),
    virtual: new UntypedFormControl(false),
    locations: new UntypedFormControl('', [Validators.required]),
    breakafter: new UntypedFormControl(null),
    tags: new UntypedFormControl([]),
    tags_required: new UntypedFormControl(false),
    tag_label: this.fb.group({
      DE: new UntypedFormControl(''),
      EN: new UntypedFormControl(''),
    }),
    tag_description: this.fb.group({
      DE: new UntypedFormControl(''),
      EN: new UntypedFormControl(''),
    }),
    short: new UntypedFormControl(false),
    starttimes: new UntypedFormControl([]),
    aireport: this.fb.group({
      report: new UntypedFormControl(''),
      threshold_negative: new UntypedFormControl('', [CustomValidators.positiveNumberValidator()]),
      threshold_positive: new UntypedFormControl('', [CustomValidators.positiveNumberValidator()]),
    })
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private auth: AuthStateService,
    private confirmationService: ConfirmationService,
    private helper: HelperService,
    public transloco: TranslocoService
  ) {
    super();
  }

  ngOnInit(): void {
    this.types = FormHelper.getSelectList(TEMPLATETYPES, null, this.transloco);
    this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
    this.buildMenu();
    forkJoin([
      this.api.getModels('ratingtypes'),
      this.api.getModels('locations'),
      this.api.getModels('files', { type: 'aireport.template'}),
      this.api.getTemplateCategories()
    ])
    .subscribe({
      next: (result: any) => {
        if (result[0] !== null && result[0].data.length > 0) {
          result[0].data.forEach((ratingtype: any) => {
            this.ratingtypes.push(new RatingtypeModel().deserialize(ratingtype));
          });
        }
        if (result[1] !== null && result[1].data.length > 0) {
          result[1].data.forEach((location: any) => {
            this.locations.push(new LocationModel().deserialize(location));
          });
        }
        if (result[2] !== null && result[2].data.length > 0) {
          result[2].data.forEach((resource: any) => {
            this.reportTemplates.push(new ResourceModel().deserialize(resource));
          });
        }
        if (result[3] !== null && result[3].data.length > 0) {
          this.optionsCategories = result[3].data;
        }
        this.buildLocationOptions();
        this.ui.setFocus();
      }
    });
    this.subscriptions.push(this.form.get('onsite').valueChanges.subscribe((value: boolean) => {
      this.buildLocationOptions();
      if (value === true) {
        this.form.get('virtual').setErrors(null);
      }
    }));
    this.subscriptions.push(this.form.get('virtual').valueChanges.subscribe((value: boolean) => {
      this.buildLocationOptions();
      if (value === true) {
        this.form.get('onsite').setErrors(null);
      }
    }));
    this.subscriptions.push(this.form.get('short').valueChanges.subscribe((value: boolean) => {
      if (value === true) {
        this.form.get('starttimes').addValidators([Validators.required]);
      } else {
        this.form.get('starttimes').clearValidators();
        this.form.get('starttimes').setErrors(null);
      }
    }));
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public onEdit(): void {
    if (this.auth.hasRight('admin.template.edit')) {
      this.startEdit();
    }
  }

  public onCancel(): void {
    this.stopEdit();
  }

  public onCreateAc(): void {
    if (this.auth.hasRight('admin.ac.create')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('template_basedata.really_copy_ac', { value: this.template.name }),
        header: this.transloco.translate('template_basedata.copy_ac'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.createAcFromTemplate(this.template.id).subscribe({
            next: (result: any) => {
              const urlData = (this.auth.getSysMode() === 'planner') ? ['/planning/orders', result.data.id] : ['/acs/planned', result.data.id];
              this.router.navigate(urlData);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      if (this.template.hasPlannedAcs()) {
        this.inheritVisible = true;
      } else {
        this.saveForm(false);
      }
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public saveForm(inherit: boolean): void {
      this.submitting = true;
      const payload = {
        name: this.form.value.name,
        language: this.form.value.language,
        template_category: this.form.value.template_category,
        ratingtype: this.form.value.ratingtype.id,
        type: this.form.value.type,
        documents_visible_observer: this.form.value.documents_visible_observer,
        mailtext: this.form.value.mailtext,
        onsite: this.form.value.onsite,
        virtual: this.form.value.virtual,
        breakafter: this.form.value.breakafter,
        locations: [...new Set(this.form.value?.locations?.map((location: LocationModel) => location.id))],
        tags: [...new Set(this.form.value?.tags?.map((tag: string) => tag.trim()))],
        tags_required: this.form.value.tags_required,
        tag_label: this.form.value.tag_label,
        tag_description: this.form.value.tag_description,
        short: this.form.value.short,
        starttimes: [...new Set(this.form.value?.starttimes?.map((starttime: string) => starttime.trim()))],
        aireport: this.form.value.aireport.report?.id || null,
        aireport_settings: {
          threshold_negative: this.form.value.aireport.threshold_negative,
          threshold_positive: this.form.value.aireport.threshold_positive
        },
        inherit
      };
      this.api.saveModel('templates', this.template.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.template.deserialize(result.data);
          this.stopEdit();
          // update optionsCategories
          if (this.optionsCategories.indexOf(this.form.value.template_category) === -1) {
            this.optionsCategories.push(this.form.value.template_category);
          }
          this.optionsCategories.sort();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
  }

  public startEdit(): void {
    if (this.template !== null) {
      this.form.patchValue({
        name: this.template.name,
        language: this.template.language,
        template_category: this.template.template_category,
        ratingtype: this.ratingtypes.find(ratingtype => ratingtype.id === this.template.ratingtype.id),
        type: this.template.type,
        mailtext: this.template.mailtext,
        documents_visible_observer: this.template.documents_visible_observer,
        onsite: this.template.onsite,
        virtual: this.template.virtual,
        locations: this.template.locations,
        breakafter: this.template.breakafter,
        tags: this.template.tags,
        tags_required: this.template.tags_required,
        tag_label: this.template.tag_label,
        tag_description: this.template.tag_description,
        short: this.template.short,
        starttimes: this.template.starttimes,
        aireport: {
          report: this.reportTemplates.find(template => template.id === this.template.aireport?.id),
          threshold_negative: this.template.aireport_settings?.threshold_negative || null,
          threshold_positive: this.template.aireport_settings?.threshold_positive || null
        }
      });
    }
    this.inEdit = true;
    this.setEdit.emit('basedata');
    this.ui.setFormFocus();
  }

  public stopEdit(): void {
    this.inEdit = false;
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  public onCopyTemplate(): void {
    if (this.auth.hasRight('admin.template.create')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('template_basedata.really_copy_template', { value: this.template.name }),
        header: this.transloco.translate('template_basedata.copy_template'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.copyTemplate(this.template.id).subscribe({
            next: (result) => {
              this.ui.toastSuccess(this.transloco.translate('template_basedata.copied', { value: this.template.name }), this.transloco.translate('common.copied'));
              this.router.navigate(['/templates', result.data.id]);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onDeleteTemplate(): void {
    if (this.auth.hasRight('admin.template.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.template.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('templates', this.template.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.template.name }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/templates']);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onDeactivateTemplate(): void {
    if (this.auth.hasRight('admin.template.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_deactivate', { value: this.template.name }),
        header: this.transloco.translate('common.really_deactivate'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deactivateTemplate(this.template.id).subscribe({
            next: (result: any) => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deactivated', { value: this.template.name }), this.transloco.translate('common.deactivated'));
              this.template.deserialize(result.data);
              this.buildMenu();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onActivateTemplate(): void {
    if (this.auth.hasRight('admin.template.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_activate', { value: this.template.name }),
        header: this.transloco.translate('common.really_activate'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.activateTemplate(this.template.id).subscribe({
            next: (result: any) => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.activated', { value: this.template.name }), this.transloco.translate('common.activated'));
              this.template.deserialize(result.data);
              this.buildMenu();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }


  public isShort(): boolean {
    return (this.inEdit !== true && this.template.isShort()) ||
      (this.inEdit === true && this.form.get('short').value === true);
  }


  protected buildMenu(): void {
    this.items = [];
    if (this.template !== null) {
      if (this.auth.hasRight('admin.template.create')) {
        this.items.push({
          label: this.transloco.translate('common.labels.copy'),
          icon: 'pi pi-copy',
          command: () => {
            this.onCopyTemplate();
          }
        });
      }
      if (this.auth.hasRight('admin.template.delete')) {
        if (this.template.status !== 'inactive') {
          this.items.push({
            label: this.transloco.translate('common.labels.deactivate'),
            icon: 'pi pi-eye-slash',
            command: () => {
              this.onDeactivateTemplate();
            }
          });
        }
        if (this.template.status === 'inactive') {
          this.items.push({
            label: this.transloco.translate('common.labels.activate'),
            icon: 'pi pi-eye',
            command: () => {
              this.onActivateTemplate();
            }
          });
        }
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.template.deletable),
          command: () => {
            this.onDeleteTemplate();
          }
        });
      }
    }
  }

  public getEditorInit(): any {
    return {
      height: 500,
      menubar: false,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
        'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
      ],
      toolbar:
        'undo redo | blocks | bold italic | \
        alignleft aligncenter alignright alignjustify | \
        bullist numlist outdent indent | removeformat | fullscreen code',
      block_formats:
        'Paragraph=p;Small=small;Preformatted=pre',
      formats: {
        small: { inline: 'small' },
        arrow: { selector : 'li', classes : 'full'}
      },
      advlist_bullet_styles: 'square disc'
    };
  }

  public getLocationNames(): Array<string> {
    return this.template.locations.map((location: LocationModel) => location.name);
  }

  private buildLocationOptions(): void {
    // filter options
    this.optionsLocations = this.locations.filter((location: LocationModel) => {
      return (this.form.get('onsite').value === true && location.virtual !== true) ||
        (this.form.get('virtual').value === true && location.virtual === true);
    });
    // set values on options
    if (this.optionsLocations.length === 0) {
      this.form.get('locations').setValue('');
      this.form.get('locations').setErrors({ required: true });
    } else if (this.optionsLocations.length === 1) {
      this.form.get('locations').setValue([this.optionsLocations[0]]);
    }
    // filter values on options
    const values = (this.form.get('locations').value || []).filter((location: LocationModel) => {
      return this.optionsLocations.find((option: LocationModel) => option.id === location.id) !== undefined;
    });
    this.form.get('locations').setValue(values);
  }

}
